import axios from "axios";
import { apiUrl, walmartAdapterUrl } from "../../constant";
import { batchConstant } from "../constants/batch.constant";
import { fetchAllMarketPlacesService } from "../../services/inventory.service";
import { InventoryConstant } from "../constants/inventory.constant";
import { getSelectList } from "../../config/utils";
import authHeader from "../../services/auth-header";

/**
 * Normalize Format A response ({ success, data, error }) to Format B
 * ({ Entity, EntityList, TotalRecords, Status, Message }) for UI compatibility.
 *
 * This lets the backend use clean Format A while the UI continues reading Format B.
 * Once all UI components are updated, this adapter can be removed.
 */
function normalizeResponse(axiosResponse) {
    const body = axiosResponse?.data;

    // Already Format B (from .NET or legacy) — pass through
    if (body?.Status !== undefined && body?.EntityList !== undefined) {
        return axiosResponse;
    }

    // Format A success: { success: true, data: { items, totalRecords, ... } }
    if (body?.success === true) {
        const d = body.data || {};
        axiosResponse.data = {
            Entity: d.message || d,
            // Preserve integrationId returned by walmart-service (e.g. auth/exchange) —
            // it would otherwise be lost since Entity collapses to d.message.
            integrationId: d.integrationId,
            EntityList: d.items || (Array.isArray(d) ? d : []),
            TotalRecords: d.totalRecords || 0,
            unique_integration_names: d.uniqueIntegrationNames || [],
            unique_types: d.uniqueTypes || [],
            Status: 200,
            Message: { AppStatusCode: 'RC_RSF', AppStatusDescription: 'Request successful' },
        };
        return axiosResponse;
    }

    // Format A error: { success: false, data: null, error: { code, message } }
    if (body?.success === false) {
        axiosResponse.data = {
            Entity: '',
            EntityList: [],
            TotalRecords: 0,
            Status: 400,
            Message: {
                AppStatusCode: body.error?.code || 'ERROR',
                AppStatusDescription: body.error?.message || 'Request failed',
            },
        };
        return axiosResponse;
    }

    return axiosResponse;
}

/**
 * Wrap axios call with response normalization
 */
function marketplaceApi(method, url, data) {
    const promise = method === 'get'
        ? axios.get(url)
        : axios.post(url, data);
    return promise.then(normalizeResponse).catch(err => {
        // Axios error — normalize the error response too
        if (err.response) {
            return normalizeResponse(err.response);
        }
        throw err;
    });
}

// ============================================================================
// Marketplace API calls (all go through walmart-service now)
// ============================================================================

export const marketPlaceListing = (companySellerId) => {
    return marketplaceApi('get', `${walmartAdapterUrl}/marketplace/types?company_seller_id=${companySellerId}`);
};

export const getLoginUrl = (data) => {
    return marketplaceApi('post', `${walmartAdapterUrl}/marketplace/auth/login-url`, data);
};

export const validateAuthCode = (data) => {
    return marketplaceApi('post', `${walmartAdapterUrl}/marketplace/auth/exchange`, data);
};

export const loadOrders = (data) => {
    return marketplaceApi('post', `${walmartAdapterUrl}/marketplace/orders/sync`, data);
};

export const marketPlaceIntegrationListing = (data) => {
    return marketplaceApi('post', `${walmartAdapterUrl}/marketplace/integrations`, data);
};

export const deleteIntegration = (data) => {
    return marketplaceApi('post', `${walmartAdapterUrl}/marketplace/integrations/update`, data);
};

export const getTrackingDetails = (data) => {
    return marketplaceApi('post', `${walmartAdapterUrl}/marketplace/ebay/tracking`, data);
};

// ============================================================================
// .NET API calls (not migrated — stay as-is)
// ============================================================================

export const syncWalmartOrders = (data) => {
    return axios.post(`${apiUrl}/api/Walmart/sync-orders`, data, { headers: authHeader() });
};

export const syncWalmartReturns = (data) => {
    return axios.post(`${apiUrl}/api/Walmart/walmart-return-upload`, data, { headers: authHeader() });
};

// ============================================================================
// Redux actions
// ============================================================================

const getFilter = (response) => ({
    type: batchConstant.GET_FILTER_STATUS,
    payload: response,
});

const marketPlaceIntegrationListingPending = () => ({
    type: batchConstant.GET_INTEGRATION_LIST_PENDING,
});

export const filterMarketPlace = (data) => {
    return (dispatch) => {
        dispatch(marketPlaceIntegrationListingPending());
        marketPlaceIntegrationListing(data).then((result) => {
            if (result.data) {
                dispatch(getFilter(result.data));
            }
        }).catch((err) => {
            console.log(err);
        });
    };
};

export const marketPlaceBackDetails = (data) => ({
    type: batchConstant.MARKET_PLACE_BACK_DETAILS,
    payload: data,
});

export const fetchAllMarketPlacesSuccess = (response) => ({
    type: InventoryConstant.FETCH_ALL_MARKETPLACES,
    payload: response,
});

export const fetchAllMarketPlaces = (data) => {
    return (dispatch) => {
        fetchAllMarketPlacesService(data).then((result) => {
            if (result.data) {
                dispatch(fetchAllMarketPlacesSuccess(getSelectList(result.data.EntityList, 'id', 'name')));
            }
        }).catch((err) => {
            console.log(err)
        });
    };
};

export const saveShopifyToken = (data) => {
    return new Promise((res, rej) => {
        return res(axios.post(`${apiUrl}/api/shopify/manage-token`, data));
    });
};
